import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import clinicService from "../../services/clinic.service";
import Turnstile, { isTurnstileConfigured } from "../common/Turnstile";
import "../reputation/reputation.css";
import "./welcome.css";

// Contact lens supply duration options.
const SUPPLY_TYPES = [
  { value: 1, label: "1 month" },
  { value: 3, label: "3 months" },
  { value: 6, label: "6 months" },
  { value: 12, label: "12 months" },
];

const FULFILLMENT_TYPES = [
  { value: "PICKUP", label: "Pickup at clinic" },
  { value: "DELIVERY", label: "Ship to my address" },
];

const COUNTRIES = ["Canada", "United States"];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Format a North American phone number as (***) ***-****.
const formatPhone = (value) => {
  const digits = (value || "").replace(/\D/g, "").slice(0, 10);
  const len = digits.length;
  if (len === 0) return "";
  if (len < 4) return `(${digits}`;
  if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const isValidPhone = (value) => (value || "").replace(/\D/g, "").length === 10;

function ContactLensReorderPage() {
  const { clinicId, patientId } = useParams();

  const [brands, setBrands] = useState([]);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    cellPhone: "",
    homePhone: "",
    brandId: "",
    supplyType: "",
    fulfillmentType: "PICKUP",
    street: "",
    apt: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileRequired = isTurnstileConfigured();
  const isShipping = formData.fulfillmentType === "DELIVERY";

  useEffect(() => {
    if (!clinicId) return;

    clinicService
      .getClBrands(clinicId)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setBrands(list.filter((b) => b.isActive !== false));
      })
      .catch((err) => {
        // 404 = no brands configured for the clinic; treat as empty.
        // eslint-disable-next-line no-console
        console.log("Failed to load contact lens brands", err);
        setBrands([]);
      });
  }, [clinicId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: formatPhone(value) }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!formData.firstName) next.firstName = "First name is required";
    if (!formData.lastName) next.lastName = "Last name is required";
    if (!formData.email) {
      next.email = "Email is required";
    } else if (!EMAIL_REGEX.test(formData.email)) {
      next.email = "Enter a valid email address";
    }
    if (!formData.cellPhone) {
      next.cellPhone = "Cell phone is required";
    } else if (!isValidPhone(formData.cellPhone)) {
      next.cellPhone = "Enter a 10-digit phone number";
    }
    if (formData.homePhone && !isValidPhone(formData.homePhone)) {
      next.homePhone = "Enter a 10-digit phone number";
    }
    if (!formData.brandId) next.brandId = "Brand is required";
    if (!formData.supplyType) next.supplyType = "Supply type is required";
    if (!formData.fulfillmentType) {
      next.fulfillmentType = "Select pickup or shipping";
    }
    if (isShipping) {
      if (!formData.street) next.street = "Street is required";
      if (!formData.city) next.city = "City is required";
      if (!formData.province) next.province = "Province is required";
      if (!formData.postalCode) next.postalCode = "Postal code is required";
      if (!formData.country) next.country = "Country is required";
    }
    if (turnstileRequired && !turnstileToken) {
      next.turnstile = "Please complete the verification challenge.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!clinicId || submitting) return;
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError("");

    const payload = {
      patientId: patientId || null,
      brandId: formData.brandId || null,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      cellPhone: formData.cellPhone,
      homePhone: formData.homePhone || null,
      type: formData.fulfillmentType,
      supplyType: Number(formData.supplyType),
      street: isShipping ? formData.street : null,
      apt: isShipping ? formData.apt || null : null,
      city: isShipping ? formData.city : null,
      province: isShipping ? formData.province : null,
      postalCode: isShipping ? formData.postalCode : null,
      country: isShipping ? formData.country : null,
      turnstileToken: turnstileToken || null,
    };

    clinicService
      .clorder(clinicId, payload)
      .then(() => setSubmitted(true))
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log("Failed to submit contact lens order", err);
        const message =
          err?.response?.data?.message ||
          "We could not submit your reorder. Please try again.";
        setSubmitError(message);
        setTurnstileToken("");
        setTurnstileResetKey((k) => k + 1);
      })
      .finally(() => setSubmitting(false));
  };

  if (submitted) {
    return (
      <div className="welcome-stack">
        <section className="rep-panel">
          <div className="rep-done">
            <div className="rep-done-mark" aria-hidden="true">
              <i className="bi bi-check-lg" />
            </div>
            <h2 className="rep-title">Reorder received</h2>
            <p className="rep-copy" style={{ marginBottom: 0 }}>
              Thank you. Your contact lens reorder has been submitted
              {isShipping
                ? " and will be shipped to the address you provided."
                : " and will be ready for pickup at the clinic."}
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="welcome-stack">
      <section className="rep-panel">
        <p className="rep-kicker">Contact lenses</p>
        <h2 className="rep-title">Contact lens reorder</h2>
        <p className="rep-copy" style={{ marginBottom: 0 }}>
          Fill in your details below and we will process your contact lens
          reorder. Fields marked required must be completed.
        </p>
      </section>

      <section className="rep-panel">
        <form onSubmit={handleSubmit} className="rep-form" noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="First name" name="firstName" value={formData.firstName} onChange={handleChange} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Last name" name="lastName" value={formData.lastName} onChange={handleChange} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                type="email"
                required
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Cell phone"
                name="cellPhone"
                value={formData.cellPhone}
                onChange={handlePhoneChange}
                type="tel"
                placeholder="(___) ___-____"
                error={Boolean(errors.cellPhone)}
                helperText={errors.cellPhone}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Home phone"
                name="homePhone"
                value={formData.homePhone}
                onChange={handlePhoneChange}
                type="tel"
                placeholder="(___) ___-____"
                error={Boolean(errors.homePhone)}
                helperText={errors.homePhone}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <h3 className="welcome-channel-title" style={{ marginTop: "0.5rem" }}>
                Lens details
              </h3>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="cl-brand-label">Contact lens brand</InputLabel>
                <Select
                  labelId="cl-brand-label"
                  label="Contact lens brand"
                  name="brandId"
                  value={formData.brandId}
                  onChange={handleChange}
                >
                  {brands.length === 0 && (
                    <MenuItem value="" disabled>
                      No brands available
                    </MenuItem>
                  )}
                  {brands.map((brand) => (
                    <MenuItem key={brand.id} value={brand.id}>
                      {brand.brandName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth size="small" required>
                <InputLabel id="cl-supply-label">Supply type</InputLabel>
                <Select
                  labelId="cl-supply-label"
                  label="Supply type"
                  name="supplyType"
                  value={formData.supplyType}
                  onChange={handleChange}
                >
                  {SUPPLY_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <h3 className="welcome-channel-title" style={{ marginTop: "0.5rem" }}>
                Fulfillment
              </h3>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl
                component="fieldset"
                error={Boolean(errors.fulfillmentType)}
                required
              >
                <FormLabel component="legend" sx={{ fontSize: "0.875rem", mb: 0.5 }}>
                  How would you like to receive your order?
                </FormLabel>
                <RadioGroup
                  row
                  name="fulfillmentType"
                  value={formData.fulfillmentType}
                  onChange={handleChange}
                >
                  {FULFILLMENT_TYPES.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio size="small" />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {errors.fulfillmentType ? (
                  <p className="rep-field-error" style={{ marginTop: 4 }}>
                    {errors.fulfillmentType}
                  </p>
                ) : null}
              </FormControl>
            </Grid>

            {isShipping ? (
              <>
                <Grid size={{ xs: 12 }}>
                  <h3 className="welcome-channel-title" style={{ marginTop: "0.5rem" }}>
                    Shipping address
                  </h3>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Street"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    required
                    error={Boolean(errors.street)}
                    helperText={errors.street}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth size="small" label="Apt/Suite" name="apt" value={formData.apt} onChange={handleChange} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    error={Boolean(errors.city)}
                    helperText={errors.city}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Province"
                    name="province"
                    value={formData.province}
                    onChange={handleChange}
                    required
                    error={Boolean(errors.province)}
                    helperText={errors.province}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Postal code"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    required
                    error={Boolean(errors.postalCode)}
                    helperText={errors.postalCode}
                    slotProps={{ htmlInput: { maxLength: 7 } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth size="small" required error={Boolean(errors.country)}>
                    <InputLabel id="cl-country-label">Country</InputLabel>
                    <Select
                      labelId="cl-country-label"
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                    >
                      {COUNTRIES.map((country) => (
                        <MenuItem key={country} value={country}>
                          {country}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            ) : (
              <Grid size={{ xs: 12 }}>
                <p className="rep-copy" style={{ margin: 0 }}>
                  Your order will be ready for pickup at the clinic. We will
                  contact you when it is available.
                </p>
              </Grid>
            )}

            {turnstileRequired ? (
              <Grid size={{ xs: 12 }}>
                <Turnstile
                  resetKey={turnstileResetKey}
                  onSuccess={(token) => {
                    setTurnstileToken(token);
                    setErrors((prev) => ({ ...prev, turnstile: undefined }));
                  }}
                  onExpire={() => setTurnstileToken("")}
                  onError={() => setTurnstileToken("")}
                />
                {errors.turnstile ? (
                  <p className="rep-field-error" style={{ marginTop: 8 }}>
                    {errors.turnstile}
                  </p>
                ) : null}
              </Grid>
            ) : null}
          </Grid>

          {submitError ? (
            <p className="rep-error" style={{ marginTop: 16 }}>
              {submitError}
            </p>
          ) : null}

          <div className="rep-actions">
            <button
              type="submit"
              className="rep-btn rep-btn--primary"
              disabled={submitting || (turnstileRequired && !turnstileToken)}
            >
              {submitting ? "Submitting…" : "Submit reorder"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default ContactLensReorderPage;
